/**
 * Device Type Template Config - 儲存每個 device type 的 enabled traits & states
 * 用於 AppFactory Device Type Templates 頁面的 Save 功能
 */
import { doc, getDocFromServer, setDoc } from 'firebase/firestore'
import { assertFirestoreEnvironmentForWrite, firestore } from './firebase'

export interface DeviceTypeTemplateConfig {
  deviceType: string
  enabledTraits: string[]
  enabledStates: string[]
  enabledCharts: string[]
  stateDisplayOverrides?: Record<string, StateDisplayOverride>
  updatedAt?: string
}

export type StateDisplayOverride = {
  icon?: string
  label?: string
  unit?: string
}

const LOCAL_STORAGE_PREFIX = 'utm_device_type_template_config_'

function storageKey(deviceType: string) {
  return `${LOCAL_STORAGE_PREFIX}${deviceType}`
}

function sanitizeStateDisplayOverrides(
  input: unknown
): Record<string, StateDisplayOverride> {
  if (!input || typeof input !== 'object') return {}
  const result: Record<string, StateDisplayOverride> = {}
  for (const [rawKey, rawValue] of Object.entries(input as Record<string, unknown>)) {
    const key = String(rawKey || '').trim()
    if (!key) continue
    const row = rawValue && typeof rawValue === 'object' ? (rawValue as Record<string, unknown>) : {}
    const icon = String(row.icon || '').trim()
    const label = String(row.label || '').trim()
    const unit = String(row.unit || '')
    const next: StateDisplayOverride = {}
    if (icon) next.icon = icon
    if (label) next.label = label
    if (unit) next.unit = unit
    if (Object.keys(next).length > 0) {
      result[key] = next
    }
  }
  return result
}

export async function loadDeviceTypeTemplateConfig(
  deviceType: string
): Promise<DeviceTypeTemplateConfig> {
  if (!deviceType) {
    return {
      deviceType: '',
      enabledTraits: [],
      enabledStates: [],
      enabledCharts: [],
      stateDisplayOverrides: {},
    }
  }

  if (!firestore) {
    const raw = localStorage.getItem(storageKey(deviceType))
    if (!raw) {
      return {
        deviceType,
        enabledTraits: [],
        enabledStates: [],
        enabledCharts: [],
        stateDisplayOverrides: {},
      }
    }
    try {
      const data = JSON.parse(raw) as DeviceTypeTemplateConfig
      return {
        deviceType,
        enabledTraits: Array.isArray(data.enabledTraits) ? data.enabledTraits : [],
        enabledStates: Array.isArray(data.enabledStates) ? data.enabledStates : [],
        enabledCharts: Array.isArray(data.enabledCharts) ? data.enabledCharts : [],
        stateDisplayOverrides: sanitizeStateDisplayOverrides(data.stateDisplayOverrides),
        updatedAt: data.updatedAt,
      }
    } catch {
      return {
        deviceType,
        enabledTraits: [],
        enabledStates: [],
        enabledCharts: [],
        stateDisplayOverrides: {},
      }
    }
  }

  const ref = doc(firestore, 'appfactory', 'config', 'deviceTypeTemplates', deviceType)
  const snap = await getDocFromServer(ref)
  if (!snap.exists()) {
    return {
      deviceType,
      enabledTraits: [],
      enabledStates: [],
      enabledCharts: [],
      stateDisplayOverrides: {},
    }
  }
  const data = snap.data() as Partial<DeviceTypeTemplateConfig>
  return {
    deviceType,
    enabledTraits: Array.isArray(data.enabledTraits) ? data.enabledTraits : [],
    enabledStates: Array.isArray(data.enabledStates) ? data.enabledStates : [],
    enabledCharts: Array.isArray(data.enabledCharts) ? data.enabledCharts : [],
    stateDisplayOverrides: sanitizeStateDisplayOverrides(data.stateDisplayOverrides),
    updatedAt: data.updatedAt,
  }
}

export async function saveDeviceTypeTemplateConfig(
  deviceType: string,
  payload: Pick<DeviceTypeTemplateConfig, 'enabledTraits' | 'enabledStates' | 'enabledCharts'> & {
    stateDisplayOverrides?: Record<string, StateDisplayOverride>
  }
): Promise<DeviceTypeTemplateConfig> {
  assertFirestoreEnvironmentForWrite('saveDeviceTypeTemplateConfig')
  const config: DeviceTypeTemplateConfig = {
    deviceType,
    enabledTraits: Array.isArray(payload.enabledTraits) ? payload.enabledTraits : [],
    enabledStates: Array.isArray(payload.enabledStates) ? payload.enabledStates : [],
    enabledCharts: Array.isArray(payload.enabledCharts) ? payload.enabledCharts : [],
    stateDisplayOverrides: sanitizeStateDisplayOverrides(payload.stateDisplayOverrides),
    updatedAt: new Date().toISOString(),
  }

  if (!firestore) {
    localStorage.setItem(storageKey(deviceType), JSON.stringify(config))
    return config
  }

  const ref = doc(firestore, 'appfactory', 'config', 'deviceTypeTemplates', deviceType)
  await setDoc(ref, config, { merge: true })
  return config
}
